<template>
  <div class="sa-detail" v-if="report">
    <!-- Detail navigation and request actions -->
    <div class="sa-detail-toolbar">
      <button
        v-if="['pending', 'assigned'].includes(report.status)"
        type="button"
        class="btn btn-outline-primary font-weight-bold"
        @click="showForwardBox = !showForwardBox"
        :disabled="isProcessing"
      >
        <CIcon name="cil-share-alt" size="sm" class="mr-1" />
        {{ $t('ticketDetail.supervisor.forwardOther') }}
      </button>
      <div v-if="canCancelTicket(report)">
        <button
          type="button"
          class="btn btn-outline-danger cancel-ticket-button font-weight-bold"
          @click="cancelTicket"
          :disabled="isProcessing"
        >
          <CIcon name="cil-ban" size="sm" class="mr-1" />
          {{ $t('ticketDetail.cancelTicketBtn') }}
        </button>
      </div>
      <button type="button" class="btn sa-detail-back" @click="$emit('close')">
        {{ text('กลับหน้ารายการ', 'Back to requests') }}
      </button>
    </div>

    <!-- Forward department box right under toolbar -->
    <div v-if="showForwardBox" class="p-3 mb-3 rounded border" style="background: #f8fafc; border-left: 5px solid #1e40af !important;">
      <h5 class="font-weight-bold text-primary mb-3">
        <CIcon name="cil-share-alt" class="mr-1" /> {{ $t('ticketDetail.supervisor.forwardOtherShort') }}
      </h5>
      <div class="form-group mb-3">
        <label class="font-weight-bold text-dark">{{ $t('ticketDetail.supervisor.pickTargetDept') }}</label>
        <select class="form-control mb-3" v-model="selectedForwardCategoryId">
          <option :value="null" disabled>{{ $t('ticketDetail.supervisor.placeholderDept') }}</option>
          <option v-for="cat in allCategories.filter(Boolean)" :key="cat._id" :value="cat._id" :disabled="isCurrentCategory(cat._id)">
            {{ cat.departmentName }} <span v-if="isCurrentCategory(cat._id)">{{ $t('ticketDetail.supervisor.yourDeptTag') }}</span>
          </option>
        </select>
        <div class="d-flex align-items-center" style="gap: 8px;">
          <CButton
            color="primary"
            class="assignment-forward text-white px-4 font-weight-bold"
            @click="forwardTicket"
            :disabled="!selectedForwardCategoryId || isProcessing"
          >
            {{ isProcessing ? $t('ticketDetail.supervisor.processing') : $t('ticketDetail.supervisor.confirmForward') }}
          </CButton>
          <CButton
            class="assignment-secondary"
            color="danger"
            variant="outline"
            @click="showForwardBox = false"
            :disabled="isProcessing"
          >
            {{ $t('mgmt.cancel') }}
          </CButton>
        </div>
      </div>
    </div>

    <!-- Hero Banner -->
    <section class="sa-detail-hero">
      <div>
        <p>{{ text('งานแจ้งซ่อม', 'Repair request') }}</p>
        <h1>{{ report.ticketNumber }}</h1>
        <span>{{ getUserName() }} • {{ getReporterAffiliation() }}</span>
      </div>
      <div class="sa-current-status">
        <strong :class="{ 'status-cancelled': report.status === 'cancelled' }">{{ statusLabel(report.status) }}</strong>
        <p>{{ nextStep }}</p>
        <small>{{ text('ข้อมูลล่าสุด', 'Updated') }} {{ formatDate(report.updatedAt || report.createdAt) }}</small>
      </div>
    </section>

    <!-- Panel 1: ความคืบหน้าการซ่อม & ข้อมูลตามขั้นตอน -->
    <section class="sa-detail-panel">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 10px;">
        <h2 class="m-0">{{ text('ความคืบหน้าการซ่อม', 'Repair progress') }}</h2>
        <div>
          <button
            type="button"
            class="btn btn-sm font-weight-bold shadow-sm d-flex align-items-center"
            style="background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; border-radius: 8px; padding: 6px 14px; transition: all 0.2s ease-in-out;"
            @click="selectStep(activeStep === 'all' ? defaultDetailStep : 'all')"
          >
            <CIcon name="cil-list" size="sm" class="mr-1 text-primary" />
            {{ activeStep === 'all' ? text('ดูแยกตามขั้นตอน', 'View by step') : text('ดูข้อมูลทั้งหมด', 'View all') }}
          </button>
        </div>
      </div>

      <ol class="sa-timeline">
        <li
          v-for="(step, index) in timeline"
          :key="step.status"
          :class="{
            reached: step.reached,
            current: step.status === report.status,
            'active-step': activeStep === step.status,
            'disabled-step': !step.reached
          }"
          :aria-current="step.status === report.status ? 'step' : null"
          v-keyboard-activate role="button" :tabindex="step.reached ? 0 : -1" :aria-disabled="String(!step.reached)" :aria-pressed="String(activeStep === step.status)"
          @click="step.reached ? selectStep(step.status) : null"
          :title="step.reached ? text('คลิกเพื่อดูขั้นตอนนี้', 'Click to view step') : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached yet')"
        >
          <span class="sa-step-number">{{ index + 1 }}</span>
          <strong>{{ step.label }}</strong>
          <small>{{ step.date ? formatDate(step.date) : (step.reached ? (step.status === 'assigned' && report.status === 'pending' ? text('รอดำเนินการ', 'Pending') : text('ไม่มีเวลาบันทึก', 'Time not recorded')) : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached')) }}</small>
        </li>
      </ol>

      <p v-if="report.status === 'cancelled'" class="alert alert-secondary">
        <strong class="text-danger">{{ text('ยกเลิกงาน:', 'Cancelled:') }}</strong> {{ report.cancelReason || text('ไม่ได้ระบุเหตุผล', 'No reason recorded') }}
      </p>

      <!-- Step Content Container -->
      <div class="sa-step-container">
        <!-- Step 1: แจ้งเรื่อง -->
        <div v-show="(activeStep === 'pending' || activeStep === 'all') && isStepReached('pending')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">1</span>
              {{ text('รายละเอียดการแจ้งซ่อม', 'Report details') }}
            </h3>
            <span class="text-muted small">{{ formatDate(getReportInformedDate()) }}</span>
          </div>

          <TicketDetailSummary
            :ticket-number="report.ticketNumber || '-'"
            :reporter="getUserName()"
            :affiliation="getReporterAffiliation()"
            :section="getSectionName()"
            :personal-phone="getPersonalPhone()"
            :office-phone="getOfficePhone()"
            :job-type="getCategoryName()"
            :work-area="getLocationFull()"
            :time-slot="getAvailableTime()"
            :on-behalf-of="getOnBehalfOf()"
            :technician-phone="getTechnicianPhone()"
          />
          <h4 class="mt-4 mb-2 font-weight-bold" style="font-size: 16px; color: #1e293b;">
            {{ text('รายละเอียดปัญหา', 'Problem description') }}
          </h4>
          <p class="sa-description" v-html="getProblemDetails()"></p>
          <p v-if="getAssetCode()" class="font-weight-bold text-danger mb-3">
            <CIcon name="cil-tag" class="mr-1" /> {{ text('รหัสครุภัณฑ์:', 'Asset code:') }} {{ getAssetCode() }}
          </p>

          <div v-if="isOutdoor()" class="mb-4">
            <label class="lbl font-weight-bold text-dark mb-2">{{ $t('ticketDetail.mapPoint') }}</label>
            <div class="map-container rounded overflow-hidden" style="height: 350px;">
              <div ref="mapContainer" style="width: 100%; height: 100%"></div>
            </div>
            <div class="mt-2 text-right">
              <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                <CIcon name="cil-map" class="mr-1" /> {{ $t('ticketDetail.openMaps') }} ↗
              </a>
            </div>
          </div>

          <div class="mt-3">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพปัญหาที่แจ้ง', 'Reported problem photo') }}</h5>
            <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" @open="openImage" @error="handleImageError" />
          </div>
        </div>

        <!-- Step 2: มอบหมายช่าง -->
        <div v-show="(activeStep === 'assigned' || activeStep === 'all') && isStepReached('assigned')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">2</span>
              {{ text('การรับเรื่องและมอบหมายงาน', 'Job assignment') }}
            </h3>
            <span class="text-muted small" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
              {{ formatDate(report.supervisorPart.dateTimeInformed) }}
            </span>
          </div>

          <!-- Postpone Active Notice inside Step 2 -->
          <div v-if="isPostponedActive" class="postpone-status-banner p-3 mb-4 rounded border" style="background: #fffbeb; border-color: #fde68a !important;">
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
              <div class="d-flex align-items-center">
                <span class="mr-2" style="font-size: 1.5rem;">⏸️</span>
                <div>
                  <strong class="text-dark d-block" style="font-size: 1rem;">
                    {{ text('สถานะงาน: เลื่อนซ่อม', 'Repair Postponed') }}
                  </strong>
                  <span class="text-muted small">
                    {{ text('ticket จะวนมาใหม่อีกครั้งในวันที่', 'Ticket will resume on') }}
                    <strong class="text-primary">{{ formatDate(report.postponedUntil) }}</strong>
                    ({{ text('เลื่อนซ่อมครั้งที่', 'Postpone count:') }} {{ postponeCount }}/2)
                    - {{ text('ยังไม่เริ่มนับ SLA จนกว่าจะถึงเวลาดังกล่าว', 'SLA countdown paused until this date') }}
                  </span>
                </div>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <button
                  v-if="!isEditingEta"
                  type="button"
                  class="btn btn-sm btn-outline-warning text-dark font-weight-bold"
                  @click="startEditEta"
                >
                  <CIcon name="cil-pencil" size="sm" class="mr-1" />
                  {{ text('แก้ไข ETA / เลื่อนซ่อม', 'Edit ETA / Postpone') }}
                </button>

              </div>
            </div>

            <!-- Editable ETA dropdown while in Step 2 -->
            <div v-if="isEditingEta" class="mt-3 pt-3 border-top d-flex align-items-center flex-wrap" style="gap: 8px;">
              <select class="form-control form-control-sm d-inline-block" style="width: auto; min-width: 200px;" v-model="etaOption">
                <option value="immediate">{{ text('ดำเนินการซ่อมได้เลย', 'Proceed immediately') }}</option>
                <option value="waiting_parts">{{ text('รออะไหล่', 'Waiting for spare parts') }}</option>
                <option value="postponed" :disabled="isPostponeLimitReached">
                  {{ postponeOptionLabel }}
                </option>
              </select>
              <input
                v-if="etaOption === 'waiting_parts'"
                type="text"
                class="form-control form-control-sm d-inline-block"
                style="width: 260px; min-width: 200px;"
                v-model="etaWaitingText"
                :placeholder="text('ระบุรอกี่วัน (เช่น รอปั๊มน้ำ 3 วัน)', 'e.g. 3 days')"
              />
              <CButton
                size="sm"
                color="primary"
                class="font-weight-bold px-3 py-1"
                style="background-color: #1e40af; border-color: #1e40af; border-radius: 6px;"
                @click="saveEta"
                :disabled="(etaOption === 'waiting_parts' && (!etaWaitingText || !etaWaitingText.trim())) || (etaOption === 'postponed' && isPostponeLimitReached) || isProcessing"
              >
                {{ isProcessing ? $t('ticketDetail.saving') : text('บันทึก', 'Save') }}
              </CButton>
              <CButton
                size="sm"
                color="secondary"
                variant="outline"
                class="py-1 px-2"
                style="border-radius: 6px;"
                @click="isEditingEta = false"
              >
                {{ $t('mgmt.cancel') }}
              </CButton>
            </div>
          </div>

          <!-- Action box if supervisor themselves is assigned as tech and pending response -->
          <div v-if="canIRespond" class="p-3 mb-4 rounded border text-center" style="background: #eff6ff; border-color: #bfdbfe !important;">
            <h5 class="font-weight-bold text-dark mb-1">{{ $t('ticketDetail.tech.newJobTitle') || 'งานใหม่ได้รับมอบหมาย' }}</h5>
            <p class="text-muted mb-3">{{ $t('ticketDetail.tech.newJobHint') || 'คุณได้รับมอบหมายให้เป็นช่างในงานนี้ ตรวจสอบรายละเอียดแล้วกดรับงานเพื่อเริ่มดำเนินการ' }}</p>
            <div class="d-flex justify-content-center flex-wrap" style="gap: 12px;">
              <CButton
                color="primary"
                class="px-4 py-2 font-weight-bold"
                style="border-radius: 8px;"
                @click="acceptWork"
                :disabled="isProcessing"
              >
                {{ isProcessing ? $t('ticketDetail.saving') : ($t('ticketDetail.tech.acceptJob') || 'รับงาน') }}
              </CButton>
              <CButton
                color="danger"
                variant="outline"
                class="px-4 py-2 font-weight-bold"
                style="border-radius: 8px;"
                @click="rejectWork"
                :disabled="isProcessing"
              >
                {{ $t('ticketDetail.tech.rejectJob') || 'ปฏิเสธงาน' }}
              </CButton>
            </div>
          </div>

          <!-- Assignment Meta Info (3 Columns) - Placed at Top -->
          <div class="assignment-meta-grid mb-4">
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-institution" size="sm" class="mr-1 text-primary" />
                {{ text('หน่วยงานรับผิดชอบ', 'Maintenance section') }}
              </span>
              <strong class="meta-value">{{ getSectionName() }}</strong>
            </div>
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-calendar" size="sm" class="mr-1 text-primary" />
                {{ text('กำหนดซ่อม', 'Repair deadline') }}
              </span>
              <strong class="meta-value" :class="{ 'text-danger': overdue }">
                <template v-if="isPostponedActive">
                  {{ text('เลื่อนซ่อม (เริ่มนับ SLA: ', 'Postponed (SLA starts: ') }}{{ formatDate(report.postponedUntil) }})
                </template>
                <template v-else>
                  {{ report.slaDeadline ? formatDate(report.slaDeadline) : text('ยังไม่ระบุ', 'Not set') }}
                </template>
              </strong>
              <small v-if="overdue" class="badge badge-danger mt-1">{{ text('เกินกำหนดซ่อม', 'Overdue') }}</small>
            </div>
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                {{ text('ระยะเวลาซ่อมโดยประมาณ', 'Estimated repair time') }}
              </span>
              <strong class="meta-value text-primary">{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
            </div>
          </div>

          <!-- Technicians Team Card (contains assignment form when pending/editing) -->
          <div class="assignment-team-card mb-4">
            <div class="team-card-header d-flex justify-content-between align-items-center flex-wrap pb-2 mb-3 border-bottom">
              <div class="d-flex align-items-center">
                <CIcon name="cil-people" class="mr-2 text-primary" style="font-size: 1.2rem;" />
                <h5 class="mb-0 font-weight-bold text-dark" style="font-size: 15px;">
                  {{ text('ทีมช่างผู้รับผิดชอบ', 'Assigned technicians') }}
                </h5>
                <span v-if="report.supervisorPart && report.supervisorPart.assignments && report.supervisorPart.assignments.length > 0" class="badge badge-pill badge-primary ml-2">
                  {{ report.supervisorPart.assignments.length }} {{ text('ท่าน', 'technicians') }}
                </span>
              </div>
              <div v-if="['assigned', 'in_progress'].includes(report.status) && !isInlineAssigning && isTicketBelongsToMySection">
                <CButton size="sm" color="outline-primary" class="font-weight-bold" @click="startInlineAssign">
                  <CIcon name="cil-pencil" size="sm" class="mr-1" />
                  {{ text('เปลี่ยนช่าง', 'Change technician') }}
                </CButton>
              </div>
            </div>

            <!-- Inline Assignment Form inside Team Card -->
            <div v-if="(report.status === 'pending' || isInlineAssigning) && isTicketBelongsToMySection" class="p-3 mb-3 rounded border bg-light">
              <h6 class="font-weight-bold text-dark mb-3">
                <CIcon name="cil-user-plus" class="mr-1 text-primary" /> {{ report.status === 'pending' ? text('มอบหมายช่างผู้รับผิดชอบ', 'Assign technicians') : text('แก้ไขการมอบหมายช่าง', 'Change technician assignment') }}
              </h6>

              <div v-for="(id, index) in selectedTechnicianIds" :key="'tech-' + index" class="mb-2">
                <div class="small font-weight-bold text-muted mb-1">
                  <span v-if="index === 0" class="badge badge-primary mr-1">{{ $t('ticketDetail.techLeader') }}</span>
                  <span v-else class="badge badge-secondary mr-1">{{ $t('ticketDetail.techHelper') }} {{ index }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <vSelect
                    class="assignment-select flex-grow-1"
                    @open="onOpenTechnicianSelect"
                    v-model="selectedTechnicianIds[index]"
                    :options="availableTechnicians"
                    :selectable="isAssigneeSelectable"
                    :disabled="isProcessing"
                    :loading="isLoadingTechnicians"
                    :reduce="tech => tech._id"
                    label="name"
                    :placeholder="$t('ticketDetail.placeholderTech')"
                    :clearable="false"
                    :filter-by="filterTechnicianOption"
                  >
                    <template #option="tech">
                      <span class="assignment-option">
                        <span class="assignment-option-name">{{ technicianOptionLabel(tech) }}</span>
                        <span class="assignment-availability" :class="technicianAvailabilityClass(tech)">{{ technicianAvailabilityLabel(tech) }}</span>
                      </span>
                    </template>
                    <template #selected-option="tech">
                      <span class="assignment-option">
                        <span class="assignment-option-name">{{ technicianOptionLabel(tech) }}</span>
                        <span class="assignment-availability" :class="technicianAvailabilityClass(tech)">{{ technicianAvailabilityLabel(tech) }}</span>
                      </span>
                    </template>
                  </vSelect>
                  <button v-if="selectedTechnicianIds.length > 1" type="button" class="btn btn-sm btn-outline-danger ml-2" @click="removeAssignee(index)" :title="$t('ticketDetail.removeAssignee')">
                    ✕
                  </button>
                </div>
              </div>

              <button v-if="selectedTechnicianIds.length < 3" type="button" class="assignment-secondary btn btn-sm btn-outline-secondary mt-1 mb-3" @click="addAssignee">
                {{ $t('ticketDetail.addAssignee') }}
              </button>

              <p v-if="assignmentSelectionError" role="alert" class="text-danger">{{ assignmentSelectionError }}</p>

              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <CButton
                  class="assignment-primary text-white font-weight-bold px-4"
                  @click="assignTask"
                  :disabled="!isValidAssignees || isProcessing"
                >
                  {{ isProcessing ? $t('ticketDetail.saving') : (!isValidAssignees ? $t('ticketDetail.selectTechFirst') : (report.status === 'pending' ? $t('ticketDetail.assignInline') : $t('ticketDetail.confirmChangeTechBtn'))) }}
                </CButton>
                <CButton
                  class="assignment-secondary"
                  v-if="report.status !== 'pending'"
                  color="danger"
                  variant="outline"
                  @click="cancelInlineAssign"
                  :disabled="isProcessing"
                >
                  {{ $t('mgmt.cancel') }}
                </CButton>
              </div>
            </div>

            <!-- Notice when ticket belongs to another section -->
            <div v-else-if="report.status === 'pending' && !isTicketBelongsToMySection" class="p-3 mb-3 rounded border bg-light">
              <div class="d-flex align-items-center">
                <CIcon name="cil-info" class="text-primary mr-2" style="font-size: 1.25rem;" />
                <div>
                  <strong class="text-dark">{{ text('ใบงานนี้สังกัดหน่วยงานอื่น', 'Ticket belongs to another section') }}</strong>
                  <div class="small text-muted mt-1">
                    {{ text(`ใบงานนี้อยู่ภายใต้การดูแลของ ${getSectionName()} ซึ่งอยู่ระหว่างรอหัวหน้างานของหน่วยงานดังกล่าวเป็นผู้มอบหมายช่าง`, `This ticket is managed by ${getSectionName()}. Pending technician assignment by the supervisor of that section.`) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Technicians List / Cards -->
            <div v-if="!isInlineAssigning">
              <div v-if="report.supervisorPart && report.supervisorPart.assignments && report.supervisorPart.assignments.length > 0" class="row tech-cards-row">
                <div
                  v-for="(item, idx) in report.supervisorPart.assignments"
                  :key="idx"
                  class="col-lg-4 col-md-6 col-12 mb-3"
                >
                  <div class="tech-card-item p-3 rounded border h-100 bg-white shadow-sm d-flex flex-column justify-content-between">
                    <div>
                      <div class="d-flex align-items-center mb-2">
                        <div class="tech-avatar mr-2">
                          <CIcon name="cil-user" />
                        </div>
                        <div class="overflow-hidden">
                          <div class="font-weight-bold text-dark text-truncate" :title="getAssigneeItemName(item)">
                            {{ getAssigneeItemName(item) }}
                          </div>
                          <small class="text-muted d-block text-truncate" v-if="item.assignee && item.assignee.jobType && item.assignee.jobType !== '-'">
                            {{ item.assignee.jobType }}
                          </small>
                        </div>
                      </div>
                      <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 4px;">
                        <span class="badge badge-primary" v-if="idx === 0">{{ $t('ticketDetail.techLeader') }}</span>
                        <span class="badge badge-secondary" v-else>{{ $t('ticketDetail.techHelper') }}</span>
                        <span class="badge badge-info">{{ text('สัดส่วน', 'Share') }} {{ item.percentage }}%</span>
                      </div>
                    </div>
                    <div class="pt-2 border-top d-flex align-items-center justify-content-between">
                      <span class="small text-muted">{{ text('สถานะการตอบรับ', 'Status') }}:</span>
                      <span v-if="item.status === 'accepted'" class="badge badge-success px-2 py-1">{{ $t('ticketDetail.tech.badgeAccepted') }}</span>
                      <span v-else-if="item.status === 'rejected'" class="badge badge-danger px-2 py-1">{{ $t('ticketDetail.tech.badgeRejected') }}</span>
                      <span v-else class="badge badge-warning text-dark px-2 py-1">{{ $t('ticketDetail.tech.badgeWaiting') }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="p-3 text-center text-muted bg-white rounded border">
                <strong>{{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}</strong>
                <div v-if="['pending', 'assigned'].includes(report.status) && isTicketBelongsToMySection" class="mt-2">
                  <CButton size="sm" color="primary" @click="startInlineAssign">
                    <CIcon name="cil-user-plus" size="sm" class="mr-1" />
                    {{ text('มอบหมายช่าง', 'Assign technician') }}
                  </CButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: กำลังซ่อม -->
        <div v-show="(activeStep === 'in_progress' || activeStep === 'all') && isStepReached('in_progress')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">3</span>
              {{ text('การดำเนินงานซ่อม', 'Repair in progress') }}
            </h3>
            <span class="text-muted small" v-if="report.technicianPart && (report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted)">
              {{ formatDate(report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted) }}
            </span>
          </div>

          <div class="p-3 rounded bg-light border mb-3">
            <div class="row align-items-center">
              <div class="col-md-6 col-12 mb-3 mb-md-0">
                <span class="text-muted small d-block mb-1">{{ text('สถานะการดำเนินงาน', 'Progress status') }}</span>
                <strong class="text-dark" style="font-size: 0.98rem;">{{ report.status === 'in_progress' ? text('ช่างกำลังดำเนินการซ่อมแซม', 'Technician is actively working on the repair') : (['done', 'welldone'].includes(report.status) ? text('ดำเนินการซ่อมเรียบร้อยแล้ว', 'Repair work completed') : text('รอช่างเริ่มดำเนินการ', 'Awaiting repair start')) }}</strong>
              </div>
              <div class="col-md-6 col-12">
                <span class="text-muted small d-block mb-1">
                  {{ text('ระยะเวลาซ่อมโดยประมาณ (ETA)', 'Estimated repair time') }}
                  <span v-if="canEditEta && (!getEtaText() || isEditingEta)" class="text-danger">*</span>
                </span>

                <div v-if="getEtaText() && !isEditingEta" class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                  <strong class="text-primary mr-2" style="font-size: 1.05rem;">{{ getEtaText() }}</strong>
                  <button
                    v-if="canEditEta"
                    type="button"
                    class="btn btn-sm btn-outline-primary py-1 px-3 font-weight-bold shadow-sm"
                    style="border-radius: 6px;"
                    @click="startEditEta"
                    :title="text('แก้ไข ETA', 'Edit ETA')"
                  >
                    <CIcon name="cil-pencil" size="sm" class="mr-1" />
                    {{ text('แก้ไข ETA', 'Edit ETA') }}
                  </button>
                </div>

                <div v-else-if="canEditEta || isEditingEta" class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                  <select class="form-control form-control-sm d-inline-block" style="width: auto; min-width: 200px;" v-model="etaOption">
                    <option value="immediate" :disabled="isSupervisorNotAssigned">{{ text('ดำเนินการซ่อมได้เลย', 'Proceed immediately') }}{{ isSupervisorNotAssigned ? ' (' + text('เฉพาะช่างผู้ปฏิบัติงาน', 'Technician only') + ')' : '' }}</option>
                    <option value="waiting_parts" :disabled="isSupervisorNotAssigned">{{ text('รออะไหล่', 'Waiting for spare parts') }}{{ isSupervisorNotAssigned ? ' (' + text('เฉพาะช่างผู้ปฏิบัติงาน', 'Technician only') + ')' : '' }}</option>
                    <option value="postponed" :disabled="isPostponeLimitReached">
                      {{ postponeOptionLabel }}
                    </option>
                  </select>
                  <input
                    v-if="etaOption === 'waiting_parts'"
                    type="text"
                    class="form-control form-control-sm d-inline-block"
                    style="width: 260px; min-width: 200px;"
                    v-model="etaWaitingText"
                    :placeholder="text('ระบุรอกี่วัน (เช่น รอปั๊มน้ำ 3 วัน)', 'e.g. 3 days')"
                  />
                  <CButton
                    size="sm"
                    color="primary"
                    class="font-weight-bold px-3 py-1"
                    style="background-color: #1e40af; border-color: #1e40af; border-radius: 6px;"
                    @click="saveEta"
                    :disabled="(etaOption === 'waiting_parts' && (!etaWaitingText || !etaWaitingText.trim())) || (etaOption === 'postponed' && isPostponeLimitReached) || isProcessing"
                  >
                    {{ isProcessing ? $t('ticketDetail.saving') : text('บันทึก', 'Save') }}
                  </CButton>
                  <CButton
                    v-if="getEtaText()"
                    size="sm"
                    color="secondary"
                    variant="outline"
                    class="py-1 px-2"
                    style="border-radius: 6px;"
                    @click="isEditingEta = false"
                  >
                    {{ $t('mgmt.cancel') }}
                  </CButton>
                </div>
                <div v-else>
                  <strong class="text-muted">{{ ['assigned', 'in_progress'].includes(report.status) ? text('รอช่างผู้รับผิดชอบกำหนดระยะเวลาซ่อม', 'Awaiting technician to set ETA') : text('ยังไม่ระบุ', 'Not set') }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Technician / Self-Assigned Supervisor Input: Full Repair Form (In Progress & Accepted/Self-Assigned) -->
          <div v-if="report.status === 'in_progress' && (myAssignmentStatus === 'accepted' || isSelfAssigned)" class="technician-workflow-container mb-4">
            <!-- If ETA is not set yet: Notice to set ETA above -->
            <div v-if="!getEtaText()" class="p-3 mb-4 rounded border text-center" style="background: #eff6ff; border: 1px solid #bfdbfe !important; color: #1e40af; font-size: 0.95rem; font-weight: 600;">
              <CIcon name="cil-clock" class="mr-1 text-primary" />
              <span>{{ text('กรุณาเลือกและบันทึกระยะเวลาดำเนินการซ่อม (ETA) ในกรอบสีเทาด้านบนเพื่อเปิดกรอบบันทึกผลการปิดงาน', 'Please select and save estimated repair time in the box above to unlock work completion form.') }}</span>
            </div>

            <!-- Full Repair Form (shown only when ETA is set) -->
            <div v-else class="p-4 rounded border mb-4" style="background: #ffffff; border-color: #e2e8f0;">

              <CForm @submit.prevent="finishWork">
                <div class="row mb-4 repair-form-row">
                  <!-- Left: Maintenance Photo Upload -->
                  <div class="col-lg-5 col-md-12 mb-3 mb-lg-0 repair-photo-col">
                    <label class="font-weight-bold text-dark mb-2">
                      <CIcon name="cil-camera" class="mr-1 text-primary" />
                      {{ $t('ticketDetail.tech.photoAfterLabel') }} <span class="text-danger">*</span>
                    </label>
                    <ImageAttachment
                      :preview="previewUrl || ''"
                      :filename="selectedFile ? selectedFile.name : ''"
                      :disabled="isProcessing"
                      :invalid="Boolean(uploadError)"
                      :watermark-options="watermarkOptions"
                      :max-count="3"
                      @change="onFileChange"
                      @remove="removeSelectedFile"
                    />
                    <div v-if="uploadError" class="text-danger small mt-1 font-weight-bold">{{ uploadError }}</div>
                  </div>

                  <!-- Right: Maintenance Details Textarea -->
                  <div class="col-lg-7 col-md-12 repair-details-col">
                    <label class="font-weight-bold text-dark mb-2">
                      <CIcon name="cil-pencil" class="mr-1 text-primary" />
                      {{ $t('ticketDetail.tech.fixDetailLabel') }} <span class="text-danger">*</span>
                    </label>
                    <CTextarea
                      v-model="maintenanceDetails"
                      rows="7"
                      class="flex-grow-1 repair-details-textarea"
                      :placeholder="$t('ticketDetail.tech.fixDetailPlaceholder')"
                      required
                    />
                  </div>
                </div>

                <!-- Spare parts section -->
                <div class="p-3 border rounded mb-4" style="background: #f8fafc;">
                  <label class="font-weight-bold text-dark mb-2">{{ $t('ticketDetail.tech.partsHeading') }} <span class="text-danger">*</span></label>
                  <div class="custom-control custom-checkbox mb-3">
                    <input id="supNoPartsUsed" v-model="noPartsUsed" type="checkbox" class="custom-control-input">
                    <label class="custom-control-label" for="supNoPartsUsed">{{ $t('ticketDetail.tech.noPartsUsed') }}</label>
                  </div>

                  <div v-if="!noPartsUsed" class="mt-3">
                    <div v-for="(part, index) in parts" :key="index" class="form-row align-items-end mb-2">
                      <div class="col-md-4 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partNameLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model="part.name" class="mb-0" :placeholder="$t('ticketDetail.tech.partNamePlaceholder')" />
                      </div>
                      <div class="col-md-2 col-6 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partQtyLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model.number="part.quantity" type="number" min="1" step="1" class="mb-0" />
                      </div>
                      <div class="col-md-3 col-6 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partPriceLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model.number="part.unitPrice" type="number" min="0" step="0.01" class="mb-0" />
                      </div>
                      <div class="col-md-2 col-8 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1 text-muted">{{ $t('ticketDetail.tech.partTotalLabel') }}</label>
                        <div class="form-control bg-light text-right font-weight-bold d-flex align-items-center justify-content-end text-dark">
                          {{ partTotal(part).toLocaleString() }}
                        </div>
                      </div>
                      <div class="col-md-1 col-4 mb-2 mb-md-0">
                        <CButton
                          color="outline-danger"
                          class="w-100 d-flex align-items-center justify-content-center"
                          :disabled="parts.length === 1"
                          @click="removePart(index)"
                          :title="$t('ticketDetail.tech.removePartTitle')"
                        >
                          <CIcon name="cil-trash" />
                        </CButton>
                      </div>
                    </div>
                    <CButton color="outline-primary" size="sm" class="mt-2" @click="addPart">
                      {{ $t('ticketDetail.tech.addPartBtn') }}
                    </CButton>
                  </div>
                </div>

                <div class="text-center mt-3">
                  <CButton
                    type="submit"
                    size="lg"
                    class="px-5 shadow-sm text-white font-weight-bold"
                    style="background-color: #1e40af; border-color: #1e40af;"
                    :disabled="isProcessing"
                  >
                    <span v-if="!isProcessing">{{ $t('ticketDetail.tech.confirmClose') }}</span>
                    <span v-else>{{ $t('ticketDetail.tech.uploading') }}</span>
                  </CButton>
                </div>
              </CForm>
            </div>
          </div>
        </div>

        <!-- Step 4: ซ่อมเสร็จ รอประเมิน -->
        <div v-show="(activeStep === 'done' || activeStep === 'all') && isStepReached('done')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">4</span>
              {{ text('ผลการซ่อมแซม', 'Repair completion') }}
            </h3>
            <span class="text-muted small" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
              {{ formatDate(report.technicianPart.dateTimeFinished) }}
            </span>
          </div>

          <div class="mb-4">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('บันทึกผลการซ่อมของช่าง', 'Technician repair notes') }}</h5>
            <p class="sa-description">{{ getMaintenanceDetails() || text('ยังไม่มีการบันทึกผลการซ่อม', 'No repair result recorded yet') }}</p>
          </div>

          <div class="mb-4">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพถ่ายหลังซ่อมเสร็จ', 'After repair photo') }}</h5>
            <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair @open="openImage" @error="handleImageError" />
            <p v-else class="text-muted small">{{ text('ยังไม่มีภาพผลการซ่อม', 'No repair result image yet') }}</p>
          </div>

          <!-- Spare parts summary table -->
          <div class="spare-parts-section my-4">
            <h5 class="font-weight-bold text-dark mb-3">{{ $t('ticketDetail.parts.title') }}</h5>
            <div v-if="hasParts()" class="table-responsive border rounded bg-white">
              <table class="table table-sm table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 70px;" class="text-center">{{ $t('ticketDetail.parts.itemNo') }}</th>
                    <th>{{ $t('ticketDetail.parts.name') }}</th>
                    <th style="width: 120px;" class="text-center">{{ $t('ticketDetail.parts.qty') }}</th>
                    <th style="width: 140px;" class="text-right">{{ $t('ticketDetail.parts.unitPrice') }}</th>
                    <th style="width: 140px;" class="text-right">{{ $t('ticketDetail.parts.lineTotal') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(part, idx) in getTicketParts()" :key="idx">
                    <td class="text-center text-muted font-weight-bold">{{ idx + 1 }}</td>
                    <td class="font-weight-bold text-dark">{{ part.name }}</td>
                    <td class="text-center font-weight-bold">{{ part.quantity.toLocaleString() }}</td>
                    <td class="text-right">{{ part.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ (part.quantity * part.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="bg-light font-weight-bold">
                    <td colspan="4" class="text-right text-dark">{{ $t('ticketDetail.parts.totalCost') }}:</td>
                    <td class="text-right text-primary" style="font-size: 1.05rem;">
                      {{ getPartsTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div v-else class="p-3 rounded bg-light border text-muted">
              <CIcon name="cil-check-circle" class="mr-1 text-success" />
              <span>{{ isNoPartsUsed() ? ($t('ticketDetail.parts.noParts') || text('ไม่มีการใช้อะไหล่', 'No parts used')) : text('ไม่มีการบันทึกรายการอะไหล่', 'No parts recorded') }}</span>
            </div>
          </div>

          <!-- If supervisor is reporter and status is done -->
          <div v-if="amIReporter && report.status === 'done'" class="p-4 my-4 rounded border text-center" style="background: #f8fafc;">
            <h5 class="font-weight-bold text-dark mb-2">{{ $t('ticketDetail.evalPlease') }}</h5>
            <div class="star-rating mb-3" role="radiogroup" :aria-label="$t('ticketDetail.evalPlease')">
              <span
                v-for="star in 5"
                :key="star"
                class="star" v-keyboard-activate role="radio" :aria-checked="String(rating === star)" :tabindex="star === (rating || 1) ? 0 : -1" :aria-label="text(star + ' จาก 5 ดาว', star + ' out of 5 stars')"
                :class="{ filled: star <= (hoverRating || rating) }"
                @mouseover="onStarHover(star)"
                @mouseleave="onStarLeave()"
                @click="onStarClick(star)"
                style="font-size: 32px; cursor: pointer; color: #cbd5e1; margin: 0 4px;"
              >★</span>
            </div>
            <CButton color="primary" size="lg" @click="submitEvaluation" :disabled="isSubmittingRating" class="px-5 font-weight-bold" style="background-color: #1e40af; border-color: #1e40af;">
              {{ isSubmittingRating ? $t('ticketDetail.saving') : $t('ticketDetail.evalSubmit') }}
            </CButton>
          </div>
        </div>

        <!-- Step 5: ประเมินแล้ว -->
        <div v-show="(activeStep === 'welldone' || activeStep === 'all') && isStepReached('welldone')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">5</span>
              {{ text('ผลการประเมินความพึงพอใจ', 'Service evaluation') }}
            </h3>
            <span class="text-muted small" v-if="report.rating && report.rating.evaluatedAt">
              {{ formatDate(report.rating.evaluatedAt) }}
            </span>
          </div>

          <div v-if="report.rating && report.rating.stars">
            <!-- 1. Star Rating Banner -->
            <div class="text-center p-3 rounded bg-light border mb-4">
              <div class="sa-rating text-warning font-weight-bold mb-1" style="font-size: 1.8rem;">
                ★ {{ report.rating.stars }} / 5
              </div>
              <p class="text-muted mb-0 small">
                {{ text('ประเมินเมื่อ', 'Rated at') }} {{ formatDate(report.rating.evaluatedAt) }}
              </p>
              <p v-if="report.rating.comment || report.rating.feedback" class="mt-2 text-dark font-italic mb-0">
                "{{ report.rating.comment || report.rating.feedback }}"
              </p>
            </div>

            <!-- 2. Side-by-side Image Comparison (รูปภาพก่อนซ่อม vs รูปภาพหลังซ่อม) -->
            <div class="mb-4">
              <h5 class="font-weight-bold text-dark mb-3" style="font-size: 1rem;">
                <CIcon name="cil-image" class="mr-1 text-primary" />
                {{ text('เปรียบเทียบรูปภาพก่อน-หลังซ่อม', 'Before & After Repair Photos') }}
              </h5>
              <div class="row">
                <!-- Left: Before Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพก่อนซ่อม', 'Before Repair Photo') }}</span>
                    </div>
                    <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพก่อนซ่อม', 'No before-repair photo') }}
                    </div>
                    <div v-if="getReportInformedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                      <span>{{ text('เวลาที่แจ้ง:', 'Reported:') }} {{ formatDate(getReportInformedDate()) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Right: After Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพหลังซ่อม', 'After Repair Photo') }}</span>

                    </div>
                    <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพหลังซ่อม', 'No after-repair photo') }}
                    </div>
                    <div v-if="getWorkFinishedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-check-circle" size="sm" class="mr-1 text-success" />
                      <span>{{ text('เวลาที่ซ่อมเสร็จ:', 'Completed:') }} {{ formatDate(getWorkFinishedDate()) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Details Grid: Technician Info & Used Spare Parts -->
            <div class="row">
              <!-- Technician Info -->
              <div class="col-md-6 mb-3">
                <div class="border rounded bg-white p-3 h-100 shadow-sm">
                  <div class="font-weight-bold text-dark border-bottom pb-2 mb-2">
                    <CIcon name="cil-user" class="mr-1 text-primary" />
                    {{ text('ข้อมูลช่างที่ซ่อม', 'Assigned Technician') }}
                  </div>
                  <div class="d-flex align-items-center pt-1">
                    <CIcon name="cil-wrench" class="mr-2 text-primary" size="lg" />
                    <div>
                      <strong class="text-dark d-block">
                        {{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}
                      </strong>
                      <small class="text-muted" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
                        {{ text('ซ่อมเสร็จเมื่อ', 'Completed at') }}: {{ formatDate(report.technicianPart.dateTimeFinished) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Used Spare Parts Info -->
              <div class="col-md-6 mb-3">
                <div class="border rounded bg-white p-3 h-100 shadow-sm">
                  <div class="font-weight-bold text-dark border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center">
                    <span>
                      <CIcon name="cil-list" class="mr-1 text-primary" />
                      {{ text('ข้อมูลอะไหล่ที่ใช้', 'Spare Parts Used') }}
                    </span>
                  </div>
                  <div v-if="hasParts()" class="pt-1">
                    <ul class="list-group list-group-flush mb-3">
                      <li v-for="(part, idx) in getTicketParts()" :key="idx" class="list-group-item bg-transparent px-0 py-2 d-flex justify-content-between align-items-center border-bottom" style="font-size: 0.95rem;">
                        <span class="text-dark font-weight-bold">
                          <span class="text-primary mr-1">{{ idx + 1 }}.</span> {{ part.name }}
                        </span>
                        <span class="badge badge-primary px-3 py-2" style="font-size: 0.88rem; font-weight: 600; border-radius: 999px;">
                          {{ part.quantity.toLocaleString() }} x {{ part.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                        </span>
                      </li>
                    </ul>
                    <div class="text-right border-top pt-2 font-weight-bold text-primary" style="font-size: 1rem;">
                      {{ $t('ticketDetail.parts.totalCost') }}: {{ getPartsTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                    </div>
                  </div>
                  <div v-else class="text-muted py-2" style="font-size: 0.95rem;">
                    <CIcon name="cil-check-circle" class="mr-1 text-success" />
                    <span>{{ isNoPartsUsed() ? ($t('ticketDetail.parts.noParts') || text('ไม่มีการใช้อะไหล่', 'No parts used')) : text('ไม่มีการบันทึกรายการอะไหล่', 'No parts recorded') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-muted text-center py-3">
            {{ report.status === 'done' ? text('อยู่ระหว่างรอการประเมินความพึงพอใจ', 'Awaiting rating') : text('ยังไม่มีข้อมูลการประเมิน', 'No evaluation data yet') }}
          </div>
        </div>
      </div>
    </section>

    <!-- Modal for Image Preview -->
    <CModal :aria-label="$t('sendReport.imageLabel')" :show.sync="imageModal" centered size="lg">
      <img :src="selectedImageUrl" class="img-fluid w-100" />
      <template #footer>
        <CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') || text('ปิด', 'Close') }}</CButton>
      </template>
    </CModal>

    <!-- Modal for setting / editing ETA -->
    <CModal :show.sync="showEtaModal" centered size="md">
      <template #header>
        <h5 class="modal-title font-weight-bold text-dark">
          <CIcon name="cil-clock" class="mr-2 text-primary" />
          {{ text('กำหนดระยะเวลาซ่อมโดยประมาณ', 'Set Estimated Repair Time') }}
        </h5>
      </template>
      <div class="p-2">
        <div class="form-group mb-3">
          <label class="font-weight-bold text-dark mb-2">
            {{ $t('ticketDetail.tech.etaLabel') || text('เลือกระยะเวลาซ่อม', 'Select repair timeframe') }}
            <span class="text-danger">*</span>
          </label>
          <CSelect
            :value="etaOption"
            @update:value="etaOption = $event"
            :options="etaOptions"
            class="mb-3"
          />
        </div>
        <div v-if="etaOption === 'custom'" class="form-group mb-3">
          <label class="font-weight-bold text-dark mb-1">{{ $t('ticketDetail.tech.etaCustomLabel') || text('ระบุระยะเวลา', 'Custom ETA text') }} <span class="text-danger">*</span></label>
          <CInput v-model="etaCustomText" :placeholder="$t('ticketDetail.tech.etaCustomPlaceholder') || text('เช่น 2 สัปดาห์ หรือ ภายในวันศุกร์นี้', 'e.g. 2 weeks')" />
        </div>
      </div>
      <template #footer>
        <CButton
          color="primary"
          class="px-4 font-weight-bold text-white"
          style="background-color: #1e40af; border-color: #1e40af;"
          @click="saveEta"
          :disabled="isProcessing"
        >
          {{ isProcessing ? $t('ticketDetail.saving') : ($t('mgmt.save') || text('บันทึก', 'Save')) }}
        </CButton>
        <CButton color="danger" variant="outline" @click="showEtaModal = false" :disabled="isProcessing">
          {{ $t('mgmt.cancel') || text('ยกเลิก', 'Cancel') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ProblemPhotoGallery from "@/components/common/ProblemPhotoGallery.vue";
import axios from "axios";
import Service from "@/service/api";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import reportDetailMixin from "@/i18n/reportDetailMixin";
import TicketDetailSummary from "@/components/common/TicketDetailSummary.vue";
import ImageAttachment from "@/components/common/ImageAttachment.vue";
import { compressImageToMax1MB, isImageFile } from "@/utils/imageCompressor";
import {
  getTicketAssetCode,
  getTicketProblemDetails,
  getTicketProblemImage,
  getTicketMaintenanceImage,
  getTicketMaintenanceImages,
  getTicketMaintenanceDetails,
  isTicketOutdoor,
  getTicketLocationFull,
  getTicketAvailableTime,
  formatTicketDate,
  getTicketUserName,
  getTicketPersonalPhone,
  getTicketOfficePhone,
  getTicketReporterAffiliation,
  getTicketSectionName,
  getTicketJobTypeName,
  getTicketOnBehalfOf,
  getTicketTechnicianPhone,
} from "@/utils/ticketViewHelper";

export default {
  directives: { keyboardActivate },
  name: "SupervisorReportDetail",
  components: { CModal, ProblemPhotoGallery, TicketDetailSummary, vSelect, ImageAttachment },
  mixins: [reportDetailMixin],
  props: {
    report: { type: Object, required: true },
  },
  data: () => ({
    activeStep: 'assigned',
    imageModal: false,
    selectedImageUrl: null,
    rating: 5,
    hoverRating: 0,
    isSubmittingRating: false,
    technicians: [],
    hasTechnicianRoster: false,
    isLoadingTechnicians: false,
    selectedTechnicianIds: [null],
    allCategories: [],
    selectedForwardCategoryId: null,
    showForwardBox: false,
    isProcessing: false,
    isInlineAssigning: false,
    map: null,
    marker: null,
    currentRepairStep: 2,
    etaOption: 'immediate',
    etaWaitingText: '',
    etaCustomText: '',
    isEtaSaved: false,
    selectedFile: null,
    selectedFiles: [],
    activeAfterPhotoIdx: 0,
    previewUrl: null,
    uploadError: null,
    maintenanceDetails: '',
    noPartsUsed: false,
    parts: [{ name: '', quantity: 1, unitPrice: 0 }],
    isEditingEta: false,
    showEtaModal: false,
  }),
  computed: {
    ...mapGetters("auth", ["profile"]),
    etaOptions() {
      return [
        { value: 'none', label: this.$t('ticketDetail.tech.etaNone') || this.text('ไม่ระบุ', 'None') },
        { value: '1-3d', label: this.$t('ticketDetail.tech.eta1_3d') || this.text('1 - 3 วัน', '1 - 3 days') },
        { value: '3-7d', label: this.$t('ticketDetail.tech.eta3_7d') || this.text('3 - 7 วัน', '3 - 7 days') },
        { value: 'waiting_parts', label: this.$t('ticketDetail.tech.etaWaitingParts') || this.text('รออะไหล่', 'Waiting for parts') },
        { value: 'custom', label: this.$t('ticketDetail.tech.etaCustomOption') || this.text('กำหนดเอง', 'Custom') }
      ];
    },
    lang() {
      return this.$store.state.locale.lang || 'th';
    },
    overdue() {
      if (this.isPostponedActive) return false;
      const isOpen = ['pending', 'assigned', 'in_progress'].includes(this.report?.status);
      return isOpen && Boolean(this.report?.slaDeadline) && new Date(this.report.slaDeadline).getTime() < Date.now();
    },
    nextStep() {
      const next = {
        pending: ['รอหน่วยงานรับเรื่องและมอบหมายช่าง', 'Awaiting assignment by the maintenance section'],
        assigned: ['รอช่างรับงานและเริ่มซ่อม', 'Awaiting technician acceptance and repair'],
        in_progress: ['ช่างกำลังดำเนินการ รอบันทึกผลการซ่อม', 'Repair is in progress; awaiting completion'],
        done: ['ช่างแจ้งซ่อมเสร็จแล้ว รอประเมินบริการ', 'Technician reported completion; awaiting rating'],
        welldone: ['มีผลการซ่อมและการประเมินบริการแล้ว', 'Repair result and service rating are available'],
        cancelled: ['สิ้นสุดการดำเนินการสำหรับรายการนี้', 'This request is no longer active'],
      };
      return (next[this.report?.status] || ['', ''])[this.lang === 'en' ? 1 : 0];
    },
    watermarkOptions() {
      const supName = this.profile
        ? `${this.profile.name || ''} ${this.profile.surname || ''}`.trim()
        : '-';
      const loc = getTicketLocationFull(this.report, this.$t.bind(this), this.$store.state.locale.lang);
      return {
        ticketNumber: this.report?.ticketNumber || '-',
        technicianName: supName || 'หัวหน้างาน (ดำเนินการซ่อมเอง)',
        location: loc || '-',
        lang: this.lang || this.$store?.state?.locale?.lang || 'th',
      };
    },
    timeline() {
      const statuses = ['pending', 'assigned', 'in_progress', 'done', 'welldone'];
      const dates = [
        this.getReportInformedDate(),
        this.getSupervisorInformedDate(),
        this.getWorkStartedDate(),
        this.getWorkFinishedDate(),
        this.report && this.report.rating && this.report.rating.evaluatedAt
      ];
      const currentIdx = statuses.indexOf(this.report && this.report.status);
      return statuses.map((status, index) => {
        let reached = false;
        if (index === 0 || index === 1) {
          reached = true;
        } else if (this.isPostponedActive) {
          reached = false;
        } else {
          reached = Boolean(dates[index]) || (currentIdx !== -1 && index <= currentIdx);
        }
        return {
          status,
          label: index === 0 ? this.text('แจ้งเรื่อง', 'Reported') : this.statusLabel(status),
          date: (this.isPostponedActive && index >= 2) ? null : dates[index],
          reached
        };
      });
    },
    isValidAssignees() {
      const selectedIds = this.selectedTechnicianIds.filter(Boolean).map(String);
      if (!selectedIds.length || selectedIds.length > 3
        || selectedIds.length !== this.selectedTechnicianIds.length
        || new Set(selectedIds).size !== selectedIds.length
        || selectedIds.some(id => !this.isAssigneeSelectable(id))) return false;
      const myId = String((this.profile && (this.profile._id || this.profile.id)) || '');
      return !selectedIds.includes(myId) || selectedIds[0] === myId;
    },
    eligibleTechnicians() {
      if (!this.hasTechnicianRoster || this.isLoadingTechnicians) return [];
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const profile = this.profile || userObj;
      const sectionId = this.assignmentRelationId(profile && profile.section);
      if (!profile || !(profile._id || profile.id) || profile.role !== 'supervisor' || profile.isActive === false || !sectionId
        || this.assignmentRelationId(this.report?.userPart?.category) !== sectionId) return [];
      const hasJobType = profile.jobType && profile.jobType !== '-';
      return this.technicians.filter(item => item && (item._id || item.id)
        && ['technician', 'supervisor'].includes(item.role) && item.isActive === true
        && this.assignmentRelationId(item.section) === sectionId
        && (!hasJobType || item.jobType === profile.jobType));
    },
    assignmentSelectionError() {
      if (this.isLoadingTechnicians || !this.selectedTechnicianIds.some(Boolean) || this.isValidAssignees) return '';
      return this.hasTechnicianRoster
        ? this.text('ผู้รับมอบหมายที่เลือกบางรายไม่มีสิทธิ์ หรือรูปแบบทีมไม่ถูกต้อง กรุณาเปลี่ยนหรือนำออกด้วยตนเอง โดยหัวหน้างานที่ซ่อมเองต้องอยู่ลำดับแรก ข้อมูลประวัติเดิมยังคงอยู่', 'Some selected assignees are ineligible or the team is invalid. Replace or remove them explicitly. Self-assignment requires you first. Existing history is unchanged.')
        : this.text('ยังตรวจสอบสิทธิ์ผู้รับมอบหมายไม่ได้ กรุณาเปิดรายการเพื่อโหลดรายชื่อใหม่ ข้อมูลที่เลือกยังคงอยู่', 'Assignment eligibility could not be verified. Reopen the selector to reload the roster. Your selections were kept.');
    },
    availableTechnicians() {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const myProfile = this.profile || userObj;
      const map = new Map();

      (this.technicians || []).forEach(item => {
        if (item && (item._id || item.id) && (item.role === 'technician' || item.role === 'supervisor')) {
          map.set(String(item._id || item.id), item);
        }
      });

      if (myProfile && (myProfile._id || myProfile.id) && ['technician', 'supervisor'].includes(myProfile.role)) {
        const myId = String(myProfile._id || myProfile.id);
        if (!map.has(myId)) {
          map.set(myId, {
            _id: myId,
            name: myProfile.name || '',
            surname: myProfile.surname || '',
            role: myProfile.role,
            department: myProfile.department || null,
            section: myProfile.section || null,
            jobType: myProfile.jobType || '-',
          });
        }
      }

      const assignments = this.report?.supervisorPart?.assignments || [];
      assignments.forEach(a => {
        if (a.assignee && typeof a.assignee === 'object' && (a.assignee._id || a.assignee.id)) {
          const aId = String(a.assignee._id || a.assignee.id);
          if (!map.has(aId)) {
            map.set(aId, a.assignee);
          }
        }
      });

      return Array.from(map.values());
    },
    amIReporter() {
      if (!this.report) return false;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const myId = String(this.profile?._id || this.profile?.id || userObj._id || userObj.id || '');
      if (!myId) return false;
      const reporterId = this.report.userPart?.user?._id
        ? String(this.report.userPart.user._id)
        : this.report.userPart?.user
          ? String(this.report.userPart.user)
          : null;
      return myId === reporterId;
    },
    myAssignment() {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const myId = String(this.profile?._id || this.profile?.id || userObj._id || userObj.id || '');
      if (!myId) return null;

      const assignments = this.report?.supervisorPart?.assignments;
      if (Array.isArray(assignments) && assignments.length > 0) {
        return assignments.find((assignment) => {
          const assignee = assignment.assignee;
          const assigneeId = assignee?._id ? assignee._id : assignee;
          return String(assigneeId) === myId;
        }) || null;
      }

      const legacyTech = this.report?.supervisorPart?.assignedTechnician;
      const legacyTechId = legacyTech?._id ? legacyTech._id : legacyTech;
      if (legacyTechId && String(legacyTechId) === myId) {
        return {
          assignee: legacyTechId,
          status: this.report?.status === 'in_progress' ? 'accepted' : 'pending'
        };
      }
      return null;
    },
    myAssignmentStatus() {
      if (!this.myAssignment) return null;
      return this.myAssignment.status || 'pending';
    },
    canIRespond() {
      if (!this.report) return false;
      if (['done', 'welldone', 'cancelled'].includes(this.report.status)) return false;
      if (this.isSelfAssigned) return false;
      return ['assigned', 'in_progress'].includes(this.report.status)
        && this.myAssignmentStatus === 'pending';
    },
    isLeadAssigned() {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const myId = String(this.profile?._id || this.profile?.id || userObj._id || userObj.id || '');
      if (!myId) return false;

      const assignments = this.report?.supervisorPart?.assignments;
      if (Array.isArray(assignments) && assignments.length > 0) {
        const lead = assignments[0]?.assignee;
        const leadId = String(lead?._id || lead || '');
        return leadId === myId;
      }
      const legacyTech = this.report?.supervisorPart?.assignedTechnician;
      const legacyId = String(legacyTech?._id || legacyTech || '');
      return legacyId === myId;
    },
    isSelfAssigned() {
      return Boolean(this.myAssignment);
    },
    isSupervisorNotAssigned() {
      return !this.isLeadAssigned;
    },
    canEditEta() {
      if (!this.report) return false;
      return ['assigned', 'in_progress'].includes(this.report.status) && this.isLeadAssigned;
    },
    isTicketBelongsToMySection() {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const profile = this.profile || userObj;
      const sectionId = this.assignmentRelationId(profile && profile.section);
      if (!sectionId) return false;
      return this.assignmentRelationId(this.report?.userPart?.category) === sectionId;
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        };
      }
      return { lat: 0, lng: 0 };
    },
  },
  created() {
    if (this.report && this.report.status === "welldone" && this.report.rating) {
      this.rating = this.report.rating.stars;
    }
    if (this.report && this.report.estimatedRepairTime && this.report.estimatedRepairTime.optionCode) {
      this.etaOption = this.report.estimatedRepairTime.optionCode;
      this.etaCustomText = this.report.estimatedRepairTime.customText || '';
      this.isEtaSaved = true;
    }
    if (this.report && this.report.technicianPart) {
      if (this.report.technicianPart.maintenanceDetails) {
        this.maintenanceDetails = this.report.technicianPart.maintenanceDetails;
      }
      if (Array.isArray(this.report.technicianPart.parts) && this.report.technicianPart.parts.length > 0) {
        this.parts = this.report.technicianPart.parts.map(p => ({
          name: p.name || '',
          quantity: p.quantity || 1,
          unitPrice: p.unitPrice || 0
        }));
      }
      if (this.report.technicianPart.noPartsUsed) {
        this.noPartsUsed = true;
      }
    }
    this.fetchTechnicians();
    this.fetchAllCategories();
    if (this.report && this.report.status === 'pending' && this.isTicketBelongsToMySection) {
      this.startInlineAssign();
    }
    this.checkAndAutoAccept();
  },
  methods: {
    assignmentRelationId(value) {
      return String(value && (value._id || value.id || value) || '');
    },
    isAssigneeSelectable(option) {
      const id = this.assignmentRelationId(option);
      return this.eligibleTechnicians.some(item => this.assignmentRelationId(item) === id);
    },
    onOpenTechnicianSelect() {
      if (!this.hasTechnicianRoster && !this.isLoadingTechnicians) {
        this.fetchTechnicians();
      }
    },
    showAssignmentValidation() {
      return Swal.fire({
        icon: 'warning', title: this.$t('ticketDetail.swalError'),
        text: this.assignmentSelectionError || this.text('กรุณาเลือกผู้รับมอบหมายที่มีสิทธิ์ 1 ถึง 3 คนโดยไม่ซ้ำกัน', 'Select one to three distinct eligible assignees.'),
      });
    },
    text(th, en) {
      return this.lang === 'en' ? en : th;
    },
    statusLabel(status) {
      const map = {
        pending: ['รอรับเรื่อง', 'Pending'],
        assigned: ['มอบหมายช่าง', 'Assign Technician'],
        in_progress: ['กำลังซ่อม', 'In progress'],
        done: ['ซ่อมเสร็จ รอประเมิน', 'Repaired · awaiting rating'],
        welldone: ['ประเมินแล้ว', 'Rated'],
        cancelled: ['ยกเลิก', 'Cancelled'],
      };
      return (map[status] || [status, status])[this.lang === 'en' ? 1 : 0];
    },
    filterTechnicianOption(option, label, search) {
      const query = (search || "").toLowerCase().trim();
      if (!query) return true;
      const text = this.technicianOptionLabel(option).toLowerCase();
      return text.includes(query);
    },
    technicianOptionLabel(tech) {
      if (!tech) return '-';
      const fullName = `${tech.name || ''} ${tech.surname || ''}`.trim();
      const departmentName = tech.department?.name || tech.section?.departmentName || '-';
      const jobType = tech.jobType || '-';
      return `${fullName} • ${departmentName} • ${jobType}`;
    },
    addAssignee() {
      if (this.selectedTechnicianIds.length < 3) {
        this.selectedTechnicianIds.push(null);
      }
    },
    removeAssignee(index) {
      if (this.selectedTechnicianIds.length > 1) {
        this.selectedTechnicianIds.splice(index, 1);
      }
    },
    async fetchTechnicians() {
      const requestId = this._technicianRosterRequestId = (this._technicianRosterRequestId || 0) + 1;
      const isCurrent = () => !this._isDestroyed && !this._isBeingDestroyed && this._technicianRosterRequestId === requestId;
      this.hasTechnicianRoster = false;
      this.isLoadingTechnicians = true;
      try {
        const url = `${process.env.VUE_APP_APP_URL}/user/technicians?includeSupervisors=true&includeAvailability=true`;
        const response = await axios.get(url);
        if (!isCurrent()) return;
        if (response.data.success && Array.isArray(response.data.data)) {
          this.technicians = Array.isArray(response.data.data)
            ? response.data.data.filter(Boolean)
            : [];
          this.hasTechnicianRoster = true;
        }
      } catch (error) {
        if (!isCurrent()) return;
        this.technicians = this.technicians.map(tech => {
          const { activeTicketCount, ...identity } = tech;
          return identity;
        });
        console.error("Error fetching technicians:", error);
      } finally {
        if (isCurrent()) this.isLoadingTechnicians = false;
      }
    },
    async assignTask() {
      if (this.isProcessing) return;
      if (!this.isValidAssignees) { await this.showAssignmentValidation(); return; }
      const assigneeIds = this.selectedTechnicianIds.slice();

      const selectedTechnicians = this.technicians.filter(tech => tech && this.selectedTechnicianIds.includes(tech._id));
      const techName = selectedTechnicians.length
        ? selectedTechnicians.map(tech => `${tech.name || ''} ${tech.surname || ''}`.trim()).join(', ')
        : this.$t('ticketDetail.selectedTechFallback');
      const isChanging = this.report.status !== 'pending';
      const result = await Swal.fire({
        title: isChanging ? this.$t('ticketDetail.confirmChangeTechTitle') : this.$t('ticketDetail.confirmAssignTitle'),
        html: isChanging
          ? this.$t('ticketDetail.confirmChangeTechHtml').replace('{name}', techName)
          : this.$t('ticketDetail.confirmAssignTechHtml').replace('{name}', techName),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        confirmButtonText: this.$t('mgmt.confirm'),
        cancelButtonText: this.$t('mgmt.cancel')
      });

      if (result.isConfirmed) {
        if (this.isProcessing) return;
        if (!this.isValidAssignees || JSON.stringify(assigneeIds) !== JSON.stringify(this.selectedTechnicianIds)) {
          await this.showAssignmentValidation(); return;
        }
        this.isProcessing = true;
        try {
          const response = await Service.ticket('patch', {
            assigneeIds,
            expectedVersion: (this.report && this.report.__v) || 0
          }, { urlAppend: `/${this.report._id}/assignments` });

          if (response.data && response.data.success) {
            const myId = String((this.profile && (this.profile._id || this.profile.id)) || '');
            if (myId && assigneeIds.map(String).includes(myId)) {
              try {
                await Service.ticket('put', { technicianId: myId }, {
                  urlAppend: `/${this.report._id}/accept`
                });
              } catch (e) {
                console.warn('Auto accept for supervisor self-assign:', e);
              }
            }
            Swal.fire(this.$t('mgmt.success'), isChanging ? this.$t('ticketDetail.changeTechOk') : this.$t('ticketDetail.assignTechOk'), 'success');
            this.isInlineAssigning = false;
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error((response.data && response.data.message) || 'Failed to update ticket');
          }
        } catch (error) {
          console.error(error);
          let errorMessage = (error.response && error.response.data && error.response.data.message)
            || (error.request ? this.$t('ticketDetail.assignmentNetworkError') : error.message)
            || this.$t('ticketDetail.assignTechFail');
          if (errorMessage && errorMessage.includes('Supervisor self-assignment must put the requesting Supervisor first')) {
            errorMessage = 'ไม่สามารถมอบงานให้ supervisor ได้';
          }
          Swal.fire(this.$t('ticketDetail.swalError'), errorMessage, 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    async assignToSelf() {
      if (this.isProcessing) return;
      const myId = String((this.profile && (this.profile._id || this.profile.id)) || '');
      if (!this.isAssigneeSelectable(myId)) { await this.showAssignmentValidation(); return; }
      if (!myId) {
        Swal.fire(this.$t('ticketDetail.swalError') || 'เกิดข้อผิดพลาด', 'ไม่พบข้อมูลผู้ใช้', 'error');
        return;
      }
      const result = await Swal.fire({
        title: this.text('ยืนยันรับซ่อมแซมด้วยตนเอง', 'Confirm Self-Assignment'),
        text: this.text('คุณต้องการรับหน้าที่เป็นช่างผู้รับผิดชอบงานนี้ใช่หรือไม่?', 'Do you want to assign yourself as the lead technician for this ticket?'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        confirmButtonText: this.text('ยืนยันรับงาน', 'Confirm'),
        cancelButtonText: this.$t('mgmt.cancel') || 'ยกเลิก'
      });

      if (result.isConfirmed) {
        if (this.isProcessing) return;
        if (!this.isAssigneeSelectable(myId)) { await this.showAssignmentValidation(); return; }
        this.isProcessing = true;
        try {
          const response = await Service.ticket('patch', {
            assigneeIds: [myId],
            expectedVersion: (this.report && this.report.__v) || 0
          }, { urlAppend: `/${this.report._id}/assignments` });

          if (response.data && response.data.success) {
            // Auto-accept so supervisor moves directly to in_progress without confirming again
            try {
              await Service.ticket('put', { technicianId: myId }, {
                urlAppend: `/${this.report._id}/accept`
              });
            } catch (acceptErr) {
              console.warn('Auto accept error:', acceptErr);
            }

            await Swal.fire({
              title: this.$t('mgmt.success') || 'สำเร็จ',
              text: this.text('รับมอบหมายงานซ่อมด้วยตนเองเรียบร้อยแล้ว เริ่มดำเนินการซ่อมได้ทันที', 'Self-assignment successful. Repair in progress.'),
              icon: 'success',
              timer: 1600,
              showConfirmButton: false
            });
            this.isInlineAssigning = false;
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error((response.data && response.data.message) || 'Failed to assign');
          }
        } catch (error) {
          console.error(error);
          const errorMessage = (error.response && error.response.data && error.response.data.message)
            || (error.request ? this.$t('ticketDetail.assignmentNetworkError') : error.message)
            || this.$t('ticketDetail.assignTechFail');
          Swal.fire(this.$t('ticketDetail.swalError') || 'เกิดข้อผิดพลาด', errorMessage, 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    async checkAndAutoAccept() {
      const myId = String((this.profile && (this.profile._id || this.profile.id)) || '');
      if (myId && this.isSelfAssigned && this.report && this.report.status === 'assigned' && this.myAssignmentStatus === 'pending') {
        try {
          await Service.ticket('put', { technicianId: myId }, {
            urlAppend: `/${this.report._id}/accept`
          });
          this.$set(this.report, 'status', 'in_progress');
          if (this.myAssignment) {
            this.$set(this.myAssignment, 'status', 'accepted');
          }
          this.$emit('refresh');
        } catch (e) {
          console.warn('Auto accept check error:', e);
        }
      }
    },
    startEditEta() {
      if (!this.canEditEta) return;
      const eta = this.report && this.report.estimatedRepairTime;
      if (eta && eta.optionCode && eta.optionCode !== 'none') {
        this.etaOption = eta.optionCode;
        this.etaWaitingText = eta.customText || '';
      } else {
        this.etaOption = this.isSupervisorNotAssigned ? 'postponed' : 'immediate';
        this.etaWaitingText = '';
      }
      this.isEditingEta = true;
    },
    openEtaModal() {
      if (!this.canEditEta) return;
      if (this.report && this.report.estimatedRepairTime && this.report.estimatedRepairTime.optionCode) {
        this.etaOption = this.report.estimatedRepairTime.optionCode;
        this.etaWaitingText = this.report.estimatedRepairTime.customText || '';
        this.etaCustomText = this.report.estimatedRepairTime.customText || '';
      } else {
        this.etaOption = 'none';
        this.etaWaitingText = '';
        this.etaCustomText = '';
      }
      this.showEtaModal = true;
    },
    async saveEta() {
      if (this.isSupervisorNotAssigned && this.etaOption !== 'postponed') {
        Swal.fire({
          icon: 'warning',
          title: this.$t('ticketDetail.swalWarning') || 'ไม่สามารถดำเนินการได้',
          text: this.text('เฉพาะช่างผู้ได้รับมอบหมายให้ปฏิบัติงานเท่านั้นที่สามารถกำหนดระยะเวลาซ่อมได้ (หัวหน้างานสามารถเลื่อนซ่อมได้)', 'Only assigned technician can set repair ETA (supervisor can postpone repair)'),
        });
        return;
      }
      if (!this.canEditEta && this.etaOption !== 'postponed') {
        Swal.fire({
          icon: 'warning',
          title: this.$t('ticketDetail.swalWarning') || 'ไม่สามารถดำเนินการได้',
          text: this.text('เฉพาะช่างผู้ได้รับมอบหมายให้ปฏิบัติงานเท่านั้นที่สามารถกำหนดระยะเวลาซ่อมได้', 'Only assigned repair personnel can set estimated repair time'),
        });
        return;
      }
      if (!this.etaOption || this.etaOption === 'none') return;


      const customDetail = this.etaOption === 'waiting_parts'
        ? (this.etaWaitingText || this.etaCustomText || '').trim()
        : (this.etaOption === 'custom' ? (this.etaCustomText || '').trim() : '');

      if (this.etaOption === 'waiting_parts' && !customDetail) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('ticketDetail.tech.etaCustomEmptyTitle') || 'กรุณาระบุระยะเวลา',
          text: this.text('กรุณากรอกรายละเอียดจำนวนวันหรือเหตุผลที่รออะไหล่', 'Please enter waiting days or details'),
          confirmButtonColor: '#f9b115',
        });
        return;
      }

      if (this.etaOption === 'waiting_parts') {
        const parsedDays = this.parseWaitingDays(customDetail);
        const targetDate = this.getNextWaitingPartsTargetDate(customDetail);
        const formattedTarget = targetDate ? this.formatDate(targetDate) : '';
        const informDate = this.getReportInformedDate();
        const informTimeStr = informDate ? new Date(informDate).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '';
        const daysInfoTh = parsedDays > 0 ? `กำหนดเวลาซ่อม (SLA) จะถูกขยายเวลาเพิ่มอีก <strong class="text-primary">${parsedDays} วัน</strong><br>เป็นสิ้นสุดวันที่` : 'กำหนดเวลาซ่อม (SLA) จะถูกขยายเวลาเป็นสิ้นสุดวันที่';
        const daysInfoEn = parsedDays > 0 ? `SLA deadline will be extended by <strong class="text-primary">${parsedDays} day(s)</strong><br>to` : 'SLA deadline will be extended to';
        const confirmRes = await Swal.fire({
          icon: 'question',
          title: this.text('ยืนยันการรออะไหล่', 'Confirm Waiting for Parts'),
          html: this.text(
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ${daysInfoTh} <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(ตามเวลาที่แจ้งซ่อมเดิม ${informTimeStr} น.)</small>
              </p>
              <p class="text-muted small mb-3">
                ระบบจะขยายกำหนดส่งมอบงานออกไปตามระยะเวลารออะไหล่
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (รออะไหล่: ${customDetail})
              </span>
            </div>`,
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ${daysInfoEn} <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(at original report time ${informTimeStr})</small>
              </p>
              <p class="text-muted small mb-3">
                SLA deadline will be extended during the waiting period.
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (Waiting for parts: ${customDetail})
              </span>
            </div>`
          ),
          showCancelButton: true,
          confirmButtonText: this.text('ยืนยันรออะไหล่', 'Confirm Waiting for Parts'),
          cancelButtonText: this.$t('mgmt.cancel'),
          confirmButtonColor: '#1e40af',
        });
        if (!confirmRes.isConfirmed) return;
      }

      if (this.etaOption === 'postponed') {
        const nextCount = (this.postponeCount || 0) + 1;
        const targetDate = this.getNextPostponeTargetDate();
        const formattedTarget = targetDate ? this.formatDate(targetDate) : '';
        const informDate = this.getReportInformedDate();
        const informTimeStr = informDate ? new Date(informDate).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '';
        const confirmRes = await Swal.fire({
          icon: 'question',
          title: this.text('ยืนยันการเลื่อนซ่อม', 'Confirm Postpone Repair'),
          html: this.text(
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ticket จะถูกเลื่อนไปเป็นวันที่ <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(ตามเวลาที่แจ้งซ่อมเดิม ${informTimeStr} น.)</small>
              </p>
              <p class="text-muted small mb-3">
                และจะเริ่มนับเวลา SLA ใหม่อีกครั้งเมื่อถึงวันและเวลาดังกล่าว
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (เลื่อนซ่อมครั้งที่ ${nextCount}/2)
              </span>
            </div>`,
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                Ticket will be postponed to <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(at original report time ${informTimeStr})</small>
              </p>
              <p class="text-muted small mb-3">
                SLA countdown will restart at that time.
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (Postpone ${nextCount} of 2)
              </span>
            </div>`
          ),
          showCancelButton: true,
          confirmButtonText: this.text('ยืนยันเลื่อนซ่อม', 'Confirm Postpone'),
          cancelButtonText: this.$t('mgmt.cancel'),
          confirmButtonColor: '#1e40af',
        });
        if (!confirmRes.isConfirmed) return;
      }

      this.isProcessing = true;
      try {
        const payload = {
          optionCode: this.etaOption,
          customText: customDetail,
          estimatedRepairTime: {
            optionCode: this.etaOption,
            customText: customDetail,
          },
        };
        const res = await Service.ticket('patch', payload, {
          urlAppend: `/${this.report._id}/estimated-time`,
        });

        if (res.data && res.data.success) {
          const updated = res.data.data;
          await Swal.fire({
            icon: 'success',
            title: this.etaOption === 'postponed' ? this.text('เลื่อนซ่อมสำเร็จ', 'Repair postponed successfully') : (this.$t('ticketDetail.tech.etaSaveOk') || 'บันทึกสำเร็จ'),
            text: this.etaOption === 'postponed'
              ? this.text('เลื่อนซ่อมเรียบร้อยแล้ว ticket จะวนมาใหม่อีกครั้งในวันพรุ่งนี้', 'Repair postponed. Ticket will resume tomorrow.')
              : this.text('บันทึกระยะเวลาซ่อมโดยประมาณเรียบร้อยแล้ว', 'Estimated repair time updated successfully'),
            timer: 1500,
            showConfirmButton: false,
          });
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          if (this.etaOption === 'postponed') {
            this.activeStep = 'assigned';
          }
          this.isEtaSaved = true;
          this.isEditingEta = false;
          this.showEtaModal = false;
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
        } else {
          throw new Error((res.data && res.data.message) || this.$t('ticketDetail.tech.etaSaveFail'));
        }
      } catch (error) {
        console.error('Save ETA error:', error);
        const errMsg = error.response?.data?.message || this.$t('ticketDetail.tech.etaSaveFail');
        Swal.fire({
          icon: 'error',
          title: this.$t('ticketDetail.swalError'),
          text: errMsg,
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async simulatePostponeReached() {
      try {
        const res = await Service.ticket('patch', {}, {
          urlAppend: `/${this.report._id}/simulate-postpone-reached`
        });
        if (res.data && res.data.success && res.data.data) {
          const updated = res.data.data;
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          this.activeStep = this.defaultDetailStep;
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
          Swal.fire({
            icon: 'info',
            title: this.text('จำลองเวลาสำเร็จ', 'Simulated successfully'),
            text: this.text('ถึงเวลาตามที่เลื่อนซ่อมแล้ว! Timeline ขั้นตอนต่อไปเปิดให้ดำเนินการ และเริ่มนับ SLA ใหม่แล้ว', 'Postpone time reached! Next timeline step is now open and SLA is actively counting.'),
            timer: 2500,
            showConfirmButton: true
          });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    },
    async resetPostpone() {
      try {
        const res = await Service.ticket('patch', {}, {
          urlAppend: `/${this.report._id}/reset-postpone`
        });
        if (res.data && res.data.success && res.data.data) {
          const updated = res.data.data;
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          this.initEta();
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
          Swal.fire({
            icon: 'success',
            title: this.text('รีเซ็ตสำเร็จ', 'Reset successfully'),
            text: this.text('รีเซ็ตการเลื่อนซ่อมเรียบร้อย สามารถทดสอบใหม่ได้', 'Postpone reset complete. You can test again.'),
            timer: 1500,
            showConfirmButton: false
          });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    },
    compressImage(file, quality = 0.8, maxWidth = 1200) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxWidth) {
                width *= maxWidth / height;
                height = maxWidth;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Compression failed'));
                return;
              }
              try {
                const name = file.name ? file.name.replace(/\.[^/.]+$/, ".jpg") : "photo.jpg";
                const compressedFile = new File([blob], name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } catch (e) {
                resolve(blob);
              }
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },
    async onFileChange(event) {
      let files = [];
      if (event && event.files && Array.isArray(event.files)) {
        files = event.files;
      } else if (event && event.target && event.target.files) {
        files = Array.from(event.target.files);
      } else if (Array.isArray(event)) {
        files = event;
      } else if (event instanceof File || (event && event.name)) {
        files = [event];
      }
      this.selectedFiles = files;
      this.selectedFile = files[0] || null;
      this.uploadError = null;

      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      if (this.selectedFile) {
        try {
          this.previewUrl = URL.createObjectURL(this.selectedFile);
        } catch (e) {
          this.previewUrl = null;
        }
      } else {
        this.previewUrl = null;
      }
    },
    removeSelectedFile(idx) {
      if (typeof idx === 'number' && this.selectedFiles.length > 0) {
        this.selectedFiles.splice(idx, 1);
        this.selectedFile = this.selectedFiles[0] || null;
      } else {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.selectedFile = null;
        this.selectedFiles = [];
        this.previewUrl = null;
      }
      this.uploadError = null;
    },
    addPart() {
      this.parts.push({ name: "", quantity: 1, unitPrice: 0 });
    },
    removePart(index) {
      if (this.parts.length > 1) this.parts.splice(index, 1);
    },
    partTotal(part) {
      return (Number(part && part.quantity) || 0) * (Number(part && part.unitPrice) || 0);
    },
    validateParts() {
      if (this.noPartsUsed) return true;
      return this.parts.length > 0 && this.parts.every(part => part
        && typeof part.name === "string"
        && part.name.trim()
        && Number.isInteger(Number(part.quantity))
        && Number(part.quantity) > 0
        && Number.isFinite(Number(part.unitPrice))
        && Number(part.unitPrice) >= 0);
    },
    async finishWork() {
      const filesToUpload = (this.selectedFiles && this.selectedFiles.length > 0)
        ? this.selectedFiles
        : (this.selectedFile ? [this.selectedFile] : []);

      if (filesToUpload.length === 0) {
        Swal.fire({
          icon: "warning",
          title: this.$t('ticketDetail.tech.needPhotoTitle') || 'จำเป็นต้องแนบภาพ',
          text: this.$t('ticketDetail.tech.needPhotoText') || 'กรุณาถ่ายภาพหรือแนบภาพหลังการซ่อมแซมเสร็จสิ้น',
          confirmButtonColor: "#f9b115",
        });
        return;
      }
      if (!this.maintenanceDetails || !this.maintenanceDetails.trim()) {
        Swal.fire({
          icon: "warning",
          title: this.$t('ticketDetail.tech.needDetailTitle') || 'จำเป็นต้องระบุรายละเอียด',
          text: this.$t('ticketDetail.tech.needDetailText') || 'กรุณากรอกบันทึกผลการซ่อมของช่าง',
          confirmButtonColor: "#f9b115",
        });
        return;
      }
      if (!this.validateParts()) {
        Swal.fire({
          icon: "warning",
          title: this.$t("ticketDetail.tech.partInvalidTitle") || 'ข้อมูลอะไหล่ไม่ถูกต้อง',
          text: this.$t("ticketDetail.tech.partInvalidText") || 'กรุณากรอกชื่อ จำนวน และราคาต่อหน่วยของอะไหล่ให้ถูกต้องครบถ้วน หรือเลือก "ไม่มีการใช้อะไหล่"',
          confirmButtonColor: "#f9b115",
        });
        return;
      }

      const result = await Swal.fire({
        icon: "question",
        title: this.$t('ticketDetail.tech.closeConfirmTitle') || 'ยืนยันการปิดงานซ่อมแซม',
        text: this.$t('ticketDetail.tech.closeConfirmText') || 'คุณแน่ใจหรือไม่ว่าต้องการบันทึกผลการซ่อมและปิดงานนี้?',
        showCancelButton: true,
        confirmButtonText: this.$t('mgmt.confirm') || 'ยืนยัน',
        cancelButtonText: this.$t('mgmt.cancel') || 'ยกเลิก',
        confirmButtonColor: "#1e40af",
        cancelButtonColor: "#dc2626",
      });

      if (!result.isConfirmed) return;

      this.isProcessing = true;
      try {
        const formData = new FormData();
        filesToUpload.slice(0, 3).forEach((file, index) => {
          const fileName = file.name || `maintenance_${index + 1}.jpg`;
          formData.append("maintenanceImages", file, fileName);
        });
        const primaryFile = filesToUpload[0];
        formData.append("maintenanceImage", primaryFile, primaryFile.name || "maintenance.jpg");
        if (primaryFile && primaryFile.watermarkInfo) {
          formData.append("photoTimestamp", primaryFile.watermarkInfo.photoTimestamp || new Date().toISOString());
          formData.append("photoSource", primaryFile.watermarkInfo.photoSource || 'camera');
        }
        formData.append("maintenanceDetails", this.maintenanceDetails);
        formData.append("spareParts", '-');
        formData.append("quantity", 0);
        formData.append("cost", 0);
        formData.append("noPartsUsed", String(this.noPartsUsed));
        formData.append("parts", JSON.stringify(this.noPartsUsed ? [] : this.parts));

        const res = await Service.ticket('put', formData, {
          urlAppend: `/${this.report._id}/finish`
        });

        if (res.data && res.data.success) {
          await Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.closeOkTitle') || 'บันทึกสำเร็จ',
            text: this.$t('ticketDetail.tech.closeOkText') || 'บันทึกผลการซ่อมและปิดงานเรียบร้อยแล้ว',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        } else {
          throw new Error((res.data && res.data.message) || "Failed to finish ticket");
        }
      } catch (error) {
        console.error("Finish work error:", error);
        const errMsg = (error.response && error.response.data && error.response.data.message) || this.$t('ticketDetail.tech.closeFail') || 'ไม่สามารถปิดงานได้';
        Swal.fire({
          icon: "error",
          title: this.$t('ticketDetail.swalError') || 'เกิดข้อผิดพลาด',
          text: errMsg,
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async acceptWork() {
      const result = await Swal.fire({
        icon: "question",
        title: this.$t('ticketDetail.tech.acceptConfirmTitle') || 'ยืนยันรับงาน',
        text: this.$t('ticketDetail.tech.acceptConfirmText') || 'คุณต้องการรับงานนี้ใช่หรือไม่?',
        showCancelButton: true,
        confirmButtonText: this.$t('ticketDetail.tech.acceptConfirmBtn') || 'รับงาน',
        cancelButtonText: this.$t('mgmt.cancel'),
        confirmButtonColor: "#1e40af",
        cancelButtonColor: "#dc2626",
      });

      if (!result.isConfirmed) return;

      this.isProcessing = true;
      try {
        const technicianId = this.profile?._id || this.profile?.id;
        const res = await Service.ticket('put', { technicianId }, {
          urlAppend: `/${this.report._id}/accept`
        });

        if (res.data.success) {
          this.report.status = "in_progress";
          this.activeStep = "in_progress";
          if (this.myAssignment) {
            this.myAssignment.status = "accepted";
          }
          await Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.acceptOkTitle') || 'รับงานเรียบร้อย',
            text: this.$t('ticketDetail.tech.acceptOkText') || 'สามารถเริ่มดำเนินการได้เลย',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh', { keepOpen: true });
        }
      } catch (error) {
        console.error(error);
        const errMsg = error.response?.data?.message || this.$t('ticketDetail.tech.acceptFail') || 'ไม่สามารถรับงานได้';
        Swal.fire({ icon: "error", title: this.$t('ticketDetail.swalError'), text: errMsg });
      } finally {
        this.isProcessing = false;
      }
    },
    async rejectWork() {
      const result = await Swal.fire({
        icon: "warning",
        title: this.$t('ticketDetail.tech.rejectConfirmTitle'),
        text: this.$t('ticketDetail.tech.rejectConfirmText'),
        input: "textarea",
        inputPlaceholder: this.$t('ticketDetail.tech.rejectPlaceholder'),
        inputValidator: (value) => {
          if (!value || !value.trim()) return this.$t('ticketDetail.tech.rejectRequired');
          return undefined;
        },
        showCancelButton: true,
        confirmButtonText: this.$t('ticketDetail.tech.rejectConfirmBtn'),
        cancelButtonText: this.$t('common.cancel') || 'ยกเลิก',
        confirmButtonColor: "#1e40af",
        cancelButtonColor: "#dc2626",
      });

      if (!result.isConfirmed) return;

      this.isProcessing = true;
      try {
        const technicianId = this.profile?._id || this.profile?.id;
        const res = await Service.ticket('put', {
          technicianId,
          rejectReason: result.value.trim(),
        }, {
          urlAppend: `/${this.report._id}/reject`,
        });

        if (res.data.success) {
          if (this.myAssignment) {
            this.myAssignment.status = "rejected";
            this.myAssignment.rejectReason = result.value.trim();
          }
          await Swal.fire({
            icon: "success",
            title: this.$t('ticketDetail.tech.rejectOkTitle'),
            text: this.$t('ticketDetail.tech.rejectOkText'),
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: this.$t('ticketDetail.swalError'),
          text: error.response?.data?.message || this.$t('ticketDetail.tech.rejectFail'),
        });
      } finally {
        this.isProcessing = false;
      }
    },
    getAssigneeItemName(item) {
      if (!item || !item.assignee) return '-';
      if (typeof item.assignee === 'object' && item.assignee !== null) {
        const name = `${item.assignee.name || ''} ${item.assignee.surname || ''}`.trim();
        if (name) return name;
      }
      const assigneeId = String(item.assignee?._id || item.assignee);
      const myId = String(this.profile?._id || this.profile?.id || '');
      if (assigneeId && assigneeId === myId && this.profile) {
        const myName = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
        return myName || this.profile.username || 'ฉัน (หัวหน้างาน)';
      }
      const found = this.technicians.find(t => String(t._id || t.id) === assigneeId);
      if (found) {
        const tName = `${found.name || ''} ${found.surname || ''}`.trim();
        return tName || found.username || '-';
      }
      return '-';
    },
    getTechnicianName() {
      const assignments = this.report?.supervisorPart?.assignments || [];
      if (assignments.length > 0) {
        const accepted = assignments.filter(a => a.status === 'accepted');
        const listToUse = (['done', 'welldone', 'in_progress'].includes(this.report?.status) && accepted.length > 0)
          ? accepted
          : assignments;

        const names = listToUse
          .map(a => {
            const assignee = a.assignee;
            if (assignee && typeof assignee === 'object') {
              const n = `${assignee.name || ''} ${assignee.surname || ''}`.trim();
              if (n) return n;
            }
            return null;
          })
          .filter(Boolean);
        if (names.length > 0) {
          return names.join(', ');
        }
      }

      const tech = this.report.supervisorPart?.assignedTechnician;
      if (!tech) return this.$t('ticketDetail.techNotYet');
      if (typeof tech === 'object') {
        const n = `${tech.name || ''} ${tech.surname || ''}`.trim();
        return n || this.$t('ticketDetail.noName');
      }
      return this.$t('ticketDetail.noName');
    },
    startInlineAssign() {
      if (this.technicians.length === 0) {
        this.fetchTechnicians();
      }
      this.showForwardBox = false;

      if (this.report && this.report.supervisorPart && this.report.supervisorPart.assignments) {
        const activeAssignments = this.report.supervisorPart.assignments.filter(a => a.status !== 'rejected');
        if (activeAssignments.length > 0) {
          this.selectedTechnicianIds = activeAssignments.map(a =>
            a.assignee?._id ? String(a.assignee._id) : String(a.assignee)
          );
        } else {
          this.selectedTechnicianIds = [null];
        }
      } else {
        this.selectedTechnicianIds = [null];
      }

      this.isInlineAssigning = true;
    },
    cancelInlineAssign() {
      this.isInlineAssigning = false;
      this.selectedTechnicianIds = [null];
    },
    async fetchAllCategories() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category`);
        if (res.data.success) {
          this.allCategories = res.data.data;
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    },
    isCurrentCategory(catId) {
      if (!this.report.userPart || !this.report.userPart.category) return false;
      const currentCat = this.report.userPart.category;
      const currentId = typeof currentCat === 'object' ? currentCat._id : currentCat;
      return String(catId) === String(currentId);
    },
    async forwardTicket() {
      if (!this.selectedForwardCategoryId) return;

      const result = await Swal.fire({
        title: this.$t('ticketDetail.supervisor.forwardConfirmTitle'),
        text: this.$t('ticketDetail.supervisor.forwardConfirmText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#dc2626',
        confirmButtonText: this.$t('mgmt.confirm'),
        cancelButtonText: this.$t('mgmt.cancel')
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          await Service.ticket('post', {
            newCategoryId: this.selectedForwardCategoryId
          }, { urlAppend: `/${this.report._id}/forward` });

          Swal.fire({ title: this.$t('mgmt.success'), text: this.$t('ticketDetail.supervisor.forwardOk'), icon: 'success' });
          this.showForwardBox = false;
          this.$emit('refresh');
          this.$emit('close');
        } catch (error) {
          console.error(error);
          Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.supervisor.forwardFail'), 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    isTicketReporter(report = this.report) {
      if (!report) return false;
      if (report._role === 'reporter') return true;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const userId = String(this.$store?.state?.auth?.profile?._id || this.$store?.state?.auth?.profile?.id || userObj._id || userObj.id || '');
      if (!userId) return false;
      const userPart = report.userPart || {};
      const rawUser = userPart.user;
      const reporterId = String(
        userPart.userId ||
        userPart.id ||
        userPart._id ||
        (typeof rawUser === 'object' && rawUser !== null ? (rawUser._id || rawUser.id) : rawUser) ||
        ''
      );
      return Boolean(reporterId && userId === reporterId);
    },
    canCancelTicket(report = this.report) {
      if (!report) return false;
      if (!this.isTicketReporter(report)) return false;
      if (['done', 'welldone', 'cancelled', 'cancel', 'in_progress'].includes(report.status)) {
        return false;
      }
      const assignments = report.supervisorPart?.assignments || [];
      const hasAcceptedAssignment = assignments.some(a => a.status === 'accepted');
      const hasAcceptedDate = Boolean(report.technicianPart?.dateTimeAccepted);
      if (hasAcceptedAssignment || hasAcceptedDate) {
        return false;
      }
      return ['pending', 'assigned'].includes(report.status);
    },
    async cancelTicket() {
      const result = await Swal.fire({
        title: this.$t('ticketDetail.cancelTicketTitle'),
        html: this.$t('ticketDetail.cancelTicketHtml'),
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: this.$t('ticketDetail.cancelPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('ticketDetail.cancelAria')
        },
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#dc2626',
        confirmButtonText: this.$t('ticketDetail.cancelConfirmBtn'),
        cancelButtonText: this.$t('ticketDetail.closeBtn'),
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return this.$t('ticketDetail.cancelReasonRequired');
          }
        }
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          const response = await Service.ticket('put', {
            userId: this.profile._id,
            cancelReason: result.value
          }, { urlAppend: `/${this.report._id}/cancel` });

          if (response.data.success) {
            Swal.fire(this.$t('mgmt.success'), this.$t('ticketDetail.cancelOk'), 'success');
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error(response.data.message || 'Failed to cancel ticket');
          }
        } catch (error) {
          console.error('Cancel ticket error:', error);
          Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.cancelFail'), 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    async submitEvaluation() {
      if (!this.rating || this.rating < 1) return;
      this.isSubmittingRating = true;
      try {
        const res = await Service.ticket('post', { stars: this.rating }, {
          urlAppend: `/${this.report._id}/rating`
        });
        if (res.data.success || res.status === 200) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('ticketDetail.swalEvalOk'),
            confirmButtonColor: '#1e40af',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: 'error', title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmittingRating = false;
      }
    },
    onStarHover(star) {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = star;
      }
    },
    onStarLeave() {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = 0;
      }
    },
    onStarClick(star) {
      if (this.report && this.report.status === 'done') {
        this.rating = star;
      }
    },
    openImage(img) {
      if (!img) return;
      this.selectedImageUrl = this.getImageUrl(img);
      this.imageModal = true;
    },
    handleImageError(e) { console.error("Image failed to load"); },
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.includes("/") ? f.split("/").pop() : f;
      return `${process.env.VUE_APP_APP_URL}/uploads/${encodeURIComponent(filename)}`;
    },
    getUserName() {
      return getTicketUserName(this.report);
    },
    getOnBehalfOf() {
      return getTicketOnBehalfOf(this.report);
    },
    getPersonalPhone() { return getTicketPersonalPhone(this.report); },
    getOfficePhone() { return getTicketOfficePhone(this.report); },
    getTechnicianPhone() {
      return getTicketTechnicianPhone(this.report, this.technicians || this.techniciansList, this.profile);
    },
    getReporterAffiliation() { return getTicketReporterAffiliation(this.report); },
    getSectionName() { return getTicketSectionName(this.report, this.lang); },
    getCategoryName() { return getTicketJobTypeName(this.report, this.lang); },
    getLocationFull() {
      return getTicketLocationFull(this.report, this.$t.bind(this), this.$store.state.locale.lang);
    },
    getAvailableTime() {
      return getTicketAvailableTime(this.report, this.lang, this.$t.bind(this));
    },
    getAssetCode() { return getTicketAssetCode(this.report); },
    getProblemDetails() { return getTicketProblemDetails(this.report); },
    getProblemImage() { return getTicketProblemImage(this.report); },
    getMaintenanceImage() { return getTicketMaintenanceImage(this.report); },
    getMaintenanceImages() { return getTicketMaintenanceImages(this.report); },
    getActiveMaintenanceImage() {
      const images = this.getMaintenanceImages();
      if (!images || images.length === 0) return this.getMaintenanceImage();
      return images[this.activeAfterPhotoIdx] || images[0];
    },
    getMaintenanceDetails() { return getTicketMaintenanceDetails(this.report); },
    getTicketParts() {
      const technicianPart = this.report?.technicianPart || {};
      if (Array.isArray(technicianPart.parts) && technicianPart.parts.length > 0) {
        return technicianPart.parts
          .filter(part => part && String(part.name || '').trim() && String(part.name || '').trim() !== '-')
          .map(part => ({
            name: String(part.name).trim(),
            quantity: Math.max(1, Number(part.quantity) || 1),
            unitPrice: Math.max(0, Number(part.unitPrice) || 0),
          }));
      }
      if (technicianPart.noPartsUsed) return [];
      const legacyName = String(technicianPart.spareParts || '').trim();
      if (legacyName && legacyName !== '-') {
        return [{
          name: legacyName,
          quantity: Math.max(1, Number(technicianPart.quantity) || 1),
          unitPrice: Math.max(0, Number(technicianPart.cost) || 0),
        }];
      }
      return [];
    },
    hasParts() {
      return this.getTicketParts().length > 0;
    },
    isNoPartsUsed() {
      return this.report?.technicianPart?.noPartsUsed === true;
    },
    getPartsTotal() {
      return this.getTicketParts().reduce(
        (total, part) => total + (part.quantity * part.unitPrice),
        0
      );
    },
    formatDate(dateString) {
      return formatTicketDate(dateString, this.lang);
    },
    isOutdoor() {
      return isTicketOutdoor(this.report);
    },

  },
};
</script>

<style scoped>
.star.filled {
  color: #f59e0b !important;
}
</style>
